import { toast } from "@/components/common/Toast";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { FaImage } from "react-icons/fa";
import { connect } from "react-redux";
import Box from "@mui/material/Box";

/**
 * The preview box. Every branch that draws a logo shares it, so a sponsor being
 * edited is capped the same way as one just picked off disk — the stored URL
 * used to render at its own intrinsic size.
 */
const PREVIEW_STYLE: React.CSSProperties = {
  width: "100%",
  maxWidth: "200px",
  maxHeight: "200px",
  objectFit: "contain",
};

/**
 * `.image-upload__upload-btn` from modal.scss — a `<label>` dressed as a button,
 * because clicking it is what opens the file picker. The `cursor: pointer` was
 * `!important` only to beat the global `label` rule that is going with the
 * cascade, so it is a plain declaration now.
 */
const uploadButtonSx = {
  display: "block",
  maxWidth: 150,
  m: "10px auto 0",
  p: "5px 8px",
  textAlign: "center",
  cursor: "pointer",
  border: "1px solid",
  borderColor: "secondary.main",
  "&:hover": { borderWidth: "1px 1px 4px 1px" },
  "&:active": { bgcolor: "info.50" },
};

/** `.btn-link` from _theme.scss was one declaration. */
const linkLabelSx = { fontSize: "0.9em", cursor: "pointer" };

interface CustomImageInputProps {
  field: { name: string; value?: any };
  setFieldValue: (name: string, value: unknown) => void;
  imageType: string;
  project?: { backdrop_url?: string; logo_url?: string; [key: string]: any };
  errorMessage?: string;
  title?: string;
  triggerImageErrorPopup: () => void;
  maxSizeErrorPopup: (phrase?: string) => void;
  inputRef?: (instance: CustomImageInput) => void;
  t: (key: string) => string;
}

interface CustomImageInputState {
  file?: File;
  imagePreviewUrl?: string;
}

class CustomImageInput extends Component<
  CustomImageInputProps,
  CustomImageInputState
> {
  fileUpload: React.RefObject<HTMLInputElement | null>;

  constructor(props: CustomImageInputProps) {
    super(props);
    this.fileUpload = React.createRef<HTMLInputElement>();
    this.showFileUpload = this.showFileUpload.bind(this);
    this.handleImageChange = this.handleImageChange.bind(this);
    this.state = {
      file: undefined,
      imagePreviewUrl: undefined,
    };
    if (typeof this.props.inputRef === "function") this.props.inputRef(this);
  }

  handleImageChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { setFieldValue, field } = this.props;
    e.preventDefault();
    const reader = new FileReader();
    const file = (e.target.files ?? [])[0];

    if (
      this.props.imageType === "hubLogo" &&
      file.size &&
      file.size > 5242880
    ) {
      return this.props.maxSizeErrorPopup("ImageSizeLimit");
    }

    if (
      this.props.imageType === "activityImage" &&
      file.size &&
      file.size > 3145728
    ) {
      return this.props.maxSizeErrorPopup("ImageSizeLimit3mb");
    }

    const validImageTypes = [
      "image/jpg",
      "image/jpeg",
      "image/png",
      "image/gif",
    ];

    if (file && !validImageTypes.includes(file.type)) {
      return this.props.triggerImageErrorPopup();
    }
    if (file) {
      reader.onloadend = () => {
        this.setState({
          file,
          imagePreviewUrl: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
      setFieldValue(field.name, file);
    }
    return true;
  }

  showFileUpload() {
    this.fileUpload.current?.click();
  }

  showPreloadImage() {
    const { field, imageType, project } = this.props;
    const { value } = field;
    const { file, imagePreviewUrl } = this.state;

    let comp: React.ReactNode = null;

    if (imageType === "avatar-upload" || imageType === "activityImage")
      return comp;
    if (file) {
      comp = <img style={PREVIEW_STYLE} src={imagePreviewUrl} alt="..." />;
    } else if (value) {
      comp = <img style={PREVIEW_STYLE} src={value} alt="..." />;
    } else if (imageType === "backdrop" && project?.backdrop_url) {
      comp = (
        <img
          style={{ width: "100%", maxHeight: "300px", objectFit: "cover" }}
          src={project.backdrop_url}
          alt="backdrop"
        />
      );
    } else if (imageType === "logo" && project?.logo_url) {
      comp = <img src={project.logo_url} alt="logo" style={PREVIEW_STYLE} />;
    } else comp = <FaImage size={70} style={{ width: "100%" }} />;

    return comp;
  }

  render() {
    const { errorMessage, title, field, imageType, t } = this.props;
    const { name } = field;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <input
          id={name}
          name={name}
          type="file"
          onChange={this.handleImageChange}
          ref={this.fileUpload}
          accept=".png, .jpg, .jpeg, .gif, .jfif"
        />
        {/*
          The wrapper used to carry `className={name}`, which was the form field
          name and not a style hook — except when the field happened to be
          called `logo`, in which case it collided with the global `.logo` rule
          and picked up a 1px border nothing asked for. The preview image caps
          itself through PREVIEW_STYLE either way.
        */}
        <Box role="presentation" onClick={this.showFileUpload}>
          {this.showPreloadImage()}
        </Box>
        {!imageType && <h5>{title}</h5>}

        {imageType === "backdrop" && (
          <Box component="label" htmlFor={imageType} sx={uploadButtonSx}>
            {t("selectImage")}
          </Box>
        )}
        {imageType === "logo" && (
          <label id="img-upload" htmlFor={imageType}>
            <Box component="span" sx={uploadButtonSx}>
              {t("uploadLogo")}
            </Box>
          </label>
        )}
        {imageType === "hubLogo" && (
          <Box component="label" htmlFor={imageType} sx={linkLabelSx}>
            {t("selectLogo")}
          </Box>
        )}
        {/* `<div color="error">` was not a valid attribute and rendered plain. */}
        {errorMessage ? (
          <Box sx={{ color: "error.main" }}>{errorMessage}</Box>
        ) : null}
      </div>
    );
  }

  // Replaces the PropTypes block; the props interface above is the contract now.
  static defaultProps: Partial<CustomImageInputProps> = {
    project: {},
    errorMessage: "",
    title: "",
  };
}

function mapDispatchToProps(
  dispatch: any,
  { t }: { t: (key: string) => string }
) {
  return {
    triggerImageErrorPopup: () => dispatch(toast.error(t("SupportedFormats"))),
    maxSizeErrorPopup: (phrase?: string) =>
      dispatch(toast.error(t(phrase || "ImageSizeLimit"))),
  };
}

export default withTranslation(["translation.forms"])(
  connect(null, mapDispatchToProps)(CustomImageInput) as any
);
